import { useState } from "react";
import {
  Box,
  Typography,
  IconButton,
  Button,
  Chip,
} from "@mui/material";
import {
  Delete,
  DragIndicator,
  QueueMusic,
  ClearAll,
} from "@mui/icons-material";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { getImageUrl } from "../../../utils/imageHelper";
import SongDetailModal from "./SongDetailModal";

const SortableItem = ({ item, onRemove, onSongClick }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.gig_queue_id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 10 : "auto",
  };

  return (
    <Box
      ref={setNodeRef}
      style={style}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        px: 2,
        py: 1,
        borderBottom: "1px solid rgba(255,255,255,0.04)",
        backgroundColor: isDragging ? "rgba(29,185,84,0.08)" : "transparent",
        "&:hover": { backgroundColor: "rgba(255,255,255,0.04)" },
      }}
    >
      <IconButton
        size="small"
        {...attributes}
        {...listeners}
        sx={{
          cursor: "grab",
          color: "rgba(255,255,255,0.3)",
          touchAction: "none",
          "&:active": { cursor: "grabbing" },
        }}
      >
        <DragIndicator fontSize="small" />
      </IconButton>

      <Box
        onClick={() => onSongClick(item)}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          flex: 1,
          minWidth: 0,
          cursor: "pointer",
        }}
      >
        <Box
          component="img"
          src={getImageUrl(item.song_image)}
          alt={item.song_name}
          sx={{
            width: 44,
            height: 44,
            borderRadius: 1,
            objectFit: "cover",
            flexShrink: 0,
          }}
        />

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: "0.875rem",
              fontWeight: 500,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {item.song_name}
          </Typography>
          <Typography
            sx={{
              fontSize: "0.75rem",
              color: "rgba(255,255,255,0.5)",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {item.artist_name || "Unknown Artist"}
          </Typography>
        </Box>
      </Box>

      <Chip
        label={`#${item.position}`}
        size="small"
        sx={{
          height: 20,
          fontSize: "0.7rem",
          backgroundColor: "rgba(29,185,84,0.15)",
          color: "#1DB954",
          flexShrink: 0,
        }}
      />

      <IconButton
        size="small"
        onClick={() => onRemove(item.gig_queue_id)}
        sx={{
          color: "rgba(255,255,255,0.3)",
          flexShrink: 0,
          "&:hover": {
            color: "#f44336",
            backgroundColor: "rgba(244,67,54,0.08)",
          },
        }}
      >
        <Delete fontSize="small" />
      </IconButton>
    </Box>
  );
};

const QueueTab = ({ queue, onRemove, onReorder, onClear }) => {
  const [clearing, setClearing] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedSong, setSelectedSong] = useState(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = queue.findIndex((q) => q.gig_queue_id === active.id);
    const newIndex = queue.findIndex((q) => q.gig_queue_id === over.id);
    const reordered = arrayMove(queue, oldIndex, newIndex);
    onReorder(reordered.map((q) => q.gig_queue_id));
  };

  const handleClear = async () => {
    setClearing(true);
    try {
      await onClear();
    } finally {
      setClearing(false);
    }
  };

  const handleSongClick = (item) => {
    setSelectedSong(item);
    setModalOpen(true);
  };

  if (queue.length === 0) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          py: 8,
          gap: 1.5,
        }}
      >
        <QueueMusic sx={{ fontSize: 48, color: "rgba(255,255,255,0.1)" }} />
        <Typography
          sx={{ color: "rgba(255,255,255,0.4)", fontSize: "0.85rem" }}
        >
          Queue is empty
        </Typography>
        <Typography
          sx={{ color: "rgba(255,255,255,0.25)", fontSize: "0.75rem" }}
        >
          Search and add songs to get started
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2,
          py: 1.5,
          borderBottom: "1px solid rgba(255,255,255,0.08)",
        }}
      >
        <Typography
          sx={{ fontSize: "0.8rem", color: "rgba(255,255,255,0.5)" }}
        >
          {queue.length} song{queue.length !== 1 ? "s" : ""} queued
        </Typography>
        <Button
          size="small"
          startIcon={<ClearAll sx={{ fontSize: 16 }} />}
          onClick={handleClear}
          disabled={clearing}
          sx={{
            fontSize: "0.75rem",
            color: "rgba(255,255,255,0.4)",
            textTransform: "none",
            "&:hover": { color: "#f44336" },
          }}
        >
          Clear All
        </Button>
      </Box>

      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          "&::-webkit-scrollbar": { width: 4 },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "rgba(255,255,255,0.1)",
            borderRadius: 2,
          },
        }}
      >
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={queue.map((q) => q.gig_queue_id)}
            strategy={verticalListSortingStrategy}
          >
            {queue.map((item) => (
              <SortableItem
                key={item.gig_queue_id}
                item={item}
                onRemove={onRemove}
                onSongClick={handleSongClick}
              />
            ))}
          </SortableContext>
        </DndContext>
      </Box>

      <SongDetailModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        songId={selectedSong?.songs_master_id}
        songData={selectedSong}
      />
    </Box>
  );
};

export default QueueTab;
